import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlaneIcon, SuitcaseIcon, BeachIcon, MountainIcon, CastleIcon, TrophyIcon, ShieldIcon, StarIcon, GlobeIcon, ClockIcon, MapPinIcon, PhoneIcon, EmailIcon, WhatsAppIcon, LocationIcon, CheckIcon, TargetIcon, LockIcon, CreditCardIcon, DeviceMobileIcon, LotusIcon } from '../components/Icons';
import api from '../services/api';
import SEO from '../components/SEO';

const Home = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentPackageSlide, setCurrentPackageSlide] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [featuredSpiritualPackages, setFeaturedSpiritualPackages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [spiritualPackagesLoading, setSpiritualPackagesLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [packagesError, setPackagesError] = useState(null);
  const [spiritualPackagesError, setSpiritualPackagesError] = useState(null);
  const [reviewsError, setReviewsError] = useState(null);
  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [bannersError, setBannersError] = useState(null);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    if (banners.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }
  };

  const prevSlide = () => {
    if (banners.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Submit to backend API
      await api.createInquiry({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });

      // On success, also open WhatsApp with the message
      const whatsappMessage = `Contact Inquiry%0A%0AName: ${formData.name}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0ASubject: ${formData.subject}%0AMessage: ${formData.message}`;
      window.open(`https://wa.me/917718838808?text=${whatsappMessage}`, '_blank');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      setError('Failed to submit your message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Icon mapping for packages
  const iconMap = {
    BeachIcon,
    MountainIcon,
    CastleIcon,
    GlobeIcon,
    TrophyIcon,
    PlaneIcon,
    StarIcon
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || GlobeIcon;
  };

  // Fetch packages, reviews, and banners on component mount
  useEffect(() => {
    fetchPackages();
    fetchSpiritualPackages();
    fetchReviews();
    fetchBanners();
  }, []);

  // Auto-slide functionality for banners
  useEffect(() => {
    if (banners.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % banners.length);
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(interval);
    }
  }, [banners.length]);

  // Detect screen size for responsive carousel
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerSlide(1); // Mobile: 1 item per slide
      } else if (window.innerWidth < 1024) {
        setItemsPerSlide(2); // Tablet: 2 items per slide
      } else {
        setItemsPerSlide(3); // Desktop: 3 items per slide
      }
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-slide functionality for packages carousel
  useEffect(() => {
    if (featuredPackages.length > itemsPerSlide) {
      const totalSlides = Math.ceil(featuredPackages.length / itemsPerSlide);
      const interval = setInterval(() => {
        setCurrentPackageSlide((prev) => (prev + 1) % totalSlides);
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(interval);
    }
  }, [featuredPackages.length, itemsPerSlide]);

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      setPackagesError(null);
      const data = await api.getPackages('all');
      // Show first 6 packages as featured
      setFeaturedPackages(data.slice(0, 6));
    } catch (err) {
      console.error('Error fetching packages:', err);
      setPackagesError('Failed to load packages. Please try again later.');
    } finally {
      setPackagesLoading(false);
    }
  };

  const fetchSpiritualPackages = async () => {
    try {
      setSpiritualPackagesLoading(true);
      setSpiritualPackagesError(null);
      const data = await api.getSpiritualPackages();
      // Show first 3 spiritual packages as featured
      setFeaturedSpiritualPackages(data.slice(0, 3));
    } catch (err) {
      console.error('Error fetching spiritual packages:', err);
      setSpiritualPackagesError('Failed to load spiritual packages. Please try again later.');
    } finally {
      setSpiritualPackagesLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      setReviewsLoading(true);
      setReviewsError(null);
      const data = await api.getReviews();
      setReviews(data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setReviewsError('Failed to load reviews. Please try again later.');
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchBanners = async () => {
    try {
      setBannersLoading(true);
      setBannersError(null);
      const data = await api.getBanners();
      setBanners(data);
      // Reset slide to 0 when banners are loaded
      if (data.length > 0) {
        setCurrentSlide(0);
      }
    } catch (err) {
      console.error('Error fetching banners:', err);
      setBannersError('Failed to load banners. Please try again later.');
      // Fallback to empty array
      setBanners([]);
    } finally {
      setBannersLoading(false);
    }
  };

  const popularDestinations = [
    { name: 'Goa', icon: BeachIcon, packages: '5+ Packages', description: 'Beach Paradise' },
    { name: 'Manali', icon: MountainIcon, packages: '12+ Packages', description: 'Mountain Retreat' },
    { name: 'Rajasthan', icon: CastleIcon, packages: '20+ Packages', description: 'Royal Heritage' },
    { name: 'Kerala', icon: GlobeIcon, packages: '18+ Packages', description: 'God\'s Own Country' },
    { name: 'Shimla', icon: MountainIcon, packages: '10+ Packages', description: 'Hill Station' },
    { name: 'Varanasi', icon: GlobeIcon, packages: '8+ Packages', description: 'Spiritual Capital' }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Choose Your Package',
      description: 'Browse through our curated travel packages or request a custom itinerary',
      icon: SuitcaseIcon
    },
    {
      step: 2,
      title: 'Consult with Expert',
      description: 'Get personalized recommendations from our travel consultants',
      icon: PhoneIcon
    },
    {
      step: 3,
      title: 'Book & Confirm',
      description: 'Secure your booking with easy payment options and instant confirmation',
      icon: CheckIcon
    },
    {
      step: 4,
      title: 'Travel & Enjoy',
      description: 'Embark on your journey with 24/7 support and enjoy your dream vacation',
      icon: PlaneIcon
    }
  ];

  const travelTips = [
    {
      title: 'Best Time to Visit India',
      description: 'October to March is ideal for most destinations, offering pleasant weather across the country.',
      icon: ClockIcon
    },
    {
      title: 'Travel Insurance',
      description: 'Always opt for comprehensive travel insurance to protect your journey and investments.',
      icon: ShieldIcon
    },
    {
      title: 'Local Currency',
      description: 'Carry some cash in Indian Rupees, though cards are widely accepted in tourist areas.',
      icon: CreditCardIcon
    },
    {
      title: 'Cultural Etiquette',
      description: 'Respect local customs and traditions. Dress modestly when visiting religious sites.',
      icon: GlobeIcon
    }
  ];

  return (
    <div className="flex-1">
      <SEO
        title="Home"
        description="Happy Yatra Travels - Discover the joy of travel. Book vacation packages, spiritual tours, and travel services across India and worldwide. Best prices, expert guidance, 24/7 support."
        keywords="travel agency India, tour packages, spiritual tours, vacation packages, holiday packages, India tours"
        canonical="/"
      />
      {/* Hero Section - Alternative Design */}

      {/* Sixth Hero Section - Slider/Carousel */}
      <section className="relative min-h-[90vh] overflow-hidden">
        {bannersLoading ? (
          <div className="h-[90vh] flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
              <p className="mt-4 text-gray-600">Loading banners...</p>
            </div>
          </div>
        ) : bannersError ? (
          <div className="h-[90vh] flex items-center justify-center">
            <div className="text-center">
              <p className="text-red-600">{bannersError}</p>
            </div>
          </div>
        ) : banners.length === 0 ? (
          <div className="h-[90vh] flex items-center justify-center bg-gradient-to-br from-primary-blue to-dark-blue">
            <div className="text-center text-white">
              <h2 className="text-4xl font-bold mb-4">Welcome to Happy Yatra Travels</h2>
              <p className="text-xl">No banners available at the moment.</p>
            </div>
          </div>
        ) : (
          /* Slider Container */
          <div className="relative h-[90vh]">
            {banners.map((banner, index) => {
              const IconComponent = getIcon(banner.icon_name || 'GlobeIcon');
              return (
                <div
                  key={banner.id}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  {/* Background Image */}
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(30, 58, 138, 0.6)), url('${banner.image_url}')`
                    }}
                  >
                    {/* Overlay Pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div 
                        className="w-full h-full"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                          backgroundSize: '60px 60px'
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 h-full flex items-center">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                      <div className="max-w-3xl">
                        {/* Icon Badge */}
                        {banner.subtitle && (
                          <div className="mb-6 inline-flex items-center gap-3 px-6 py-3 bg-white/20 backdrop-blur-md rounded-full border border-white/30">
                            {banner.icon_name && (
                              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                <IconComponent className="w-6 h-6 text-yellow-400" />
                              </div>
                            )}
                            <span className="text-white font-bold text-sm md:text-base">{banner.subtitle}</span>
                          </div>
                        )}

                        {/* Title */}
                        <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 text-white leading-tight">
                          {banner.title}
                        </h2>

                        {/* Description */}
                        {banner.description && (
                          <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                            {banner.description}
                          </p>
                        )}

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <Link 
                            to="/packages" 
                            className="px-8 py-4 bg-yellow-400 text-gray-900 rounded-xl font-bold hover:bg-yellow-300 transform hover:scale-105 transition-all duration-300 shadow-2xl text-center"
                          >
                            View Package Details
                          </Link>
                          <a 
                            href={`https://wa.me/917718838808?text=Hi, I'm interested in ${banner.title}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="px-8 py-4 bg-white/20 backdrop-blur-md text-white rounded-xl font-bold hover:bg-white/30 border-2 border-white/50 transform hover:scale-105 transition-all duration-300 text-center"
                          >
                            <span className="flex items-center gap-2 justify-center">
                              <WhatsAppIcon className="w-5 h-5" />
                              Get Quote
                            </span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Navigation Arrows */}
            {banners.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 border border-white/30"
                  aria-label="Previous slide"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 border border-white/30"
                  aria-label="Next slide"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Slide Indicators */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? 'bg-yellow-400 w-8'
                          : 'bg-white/50 hover:bg-white/70'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Bottom Stats Bar */}
        <div className="relative z-10 bg-white/95 backdrop-blur-sm py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <GlobeIcon className="w-10 h-10 text-primary-blue" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">100+</div>
                <div className="text-sm text-gray-600 font-semibold">Destinations</div>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <TrophyIcon className="w-10 h-10 text-primary-orange" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">5+</div>
                <div className="text-sm text-gray-600 font-semibold">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <StarIcon className="w-10 h-10 text-primary-blue" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">1K+</div>
                <div className="text-sm text-gray-600 font-semibold">Happy Travelers</div>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <ShieldIcon className="w-10 h-10 text-primary-orange" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">98%</div>
                <div className="text-sm text-gray-600 font-semibold">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            Why Choose Us?
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Experience travel excellence with our trusted services
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { Icon: PlaneIcon, title: 'Best Prices', desc: 'Competitive pricing with no hidden charges' },
              { Icon: TrophyIcon, title: 'Expert Guidance', desc: 'Experienced travel consultants to plan your trip' },
              { Icon: ShieldIcon, title: 'Safe & Secure', desc: 'Your safety and satisfaction is our priority' },
              { Icon: StarIcon, title: '24/7 Support', desc: 'Round-the-clock assistance during your journey' }
            ].map((feature, index) => {
              const IconComponent = feature.Icon;
              return (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl text-center transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl border border-gray-100"
                >
                  <div className="flex justify-center mb-4 text-primary-blue">
                    <IconComponent className="w-12 h-12" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary-blue mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Packages Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            Featured Packages
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Handpicked destinations for your perfect vacation
          </p>
          {packagesLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
              <p className="mt-4 text-gray-600">Loading packages...</p>
            </div>
          ) : packagesError ? (
            <div className="text-center py-12">
              <p className="text-red-600">{packagesError}</p>
            </div>
          ) : featuredPackages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No packages available at the moment.</p>
            </div>
          ) : (
            <div className="relative mb-12 py-8">
              {/* Packages Carousel - Shows 1 on mobile, 2 on tablet, 3 on desktop */}
              <div className="relative overflow-hidden w-full py-4">
                <div 
                  className="flex transition-transform duration-500 ease-in-out w-full"
                  style={{ 
                    transform: `translateX(-${currentPackageSlide * 100}%)`
                  }}
                >
                  {/* Group packages into slides - responsive based on screen size */}
                  {Array.from({ length: Math.ceil(featuredPackages.length / itemsPerSlide) }).map((_, slideIndex) => (
                    <div 
                      key={slideIndex}
                      className="min-w-full flex-shrink-0 w-full"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {featuredPackages.slice(slideIndex * itemsPerSlide, slideIndex * itemsPerSlide + itemsPerSlide).map((pkg) => (
                          <div 
                            key={pkg.id}
                            className="bg-white rounded-2xl overflow-hidden transform hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                            onClick={() => navigate(`/packages/${pkg.id}`)}
                          >
                            {pkg.image_url ? (
                              <div className="h-48 w-full overflow-hidden">
                                <img 
                                  src={pkg.image_url} 
                                  alt={pkg.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="bg-gradient-to-br from-light-blue to-light-orange p-12 text-center">
                                <div className="flex justify-center text-primary-blue">
                                  <GlobeIcon className="w-20 h-20" />
                                </div>
                              </div>
                            )}
                            <div className="p-6">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3 hover:text-primary-orange transition-colors">{pkg.title}</h3>
                              <p className="text-primary-blue font-semibold mb-2 flex items-center gap-2">
                                <MapPinIcon className="w-4 h-4" />
                                {pkg.destination}
                              </p>
                              <p className="text-gray-600 text-sm mb-4 flex items-center gap-2">
                                <ClockIcon className="w-4 h-4" />
                                {pkg.duration}
                              </p>
                              <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">{pkg.description}</p>
                              
                              {pkg.highlights && Array.isArray(pkg.highlights) && pkg.highlights.length > 0 && (
                                <div className="mb-4">
                                  <div className="flex flex-wrap gap-2">
                                    {pkg.highlights.slice(0, 3).map((highlight, idx) => (
                                      <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                        {highlight}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t border-gray-200">
                                <div>
                                  <span className="text-2xl font-bold text-primary-orange">{pkg.price}</span>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/packages/${pkg.id}`);
                                  }}
                                  className="px-6 py-2.5 bg-primary-orange text-white rounded-lg font-semibold hover:bg-primary-blue transition-all duration-300 text-sm whitespace-nowrap"
                                >
                                  View Details
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              {featuredPackages.length > itemsPerSlide && (
                <>
                  <button
                    onClick={() => {
                      const totalSlides = Math.ceil(featuredPackages.length / itemsPerSlide);
                      setCurrentPackageSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
                    }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-primary-blue p-3 rounded-full transition-all duration-300 z-10"
                    aria-label="Previous packages"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      const totalSlides = Math.ceil(featuredPackages.length / itemsPerSlide);
                      setCurrentPackageSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
                    }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-primary-blue p-3 rounded-full transition-all duration-300 z-10"
                    aria-label="Next packages"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Dots Indicator */}
              {featuredPackages.length > itemsPerSlide && (
                <div className="flex justify-center gap-2 mt-6">
                  {Array.from({ length: Math.ceil(featuredPackages.length / itemsPerSlide) }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPackageSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentPackageSlide
                          ? 'bg-primary-orange w-8'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="text-center">
            <Link 
              to="/packages" 
              className="inline-block px-8 py-4 bg-primary-blue text-white rounded-xl font-semibold hover:bg-dark-blue transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              View All Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Spiritual Packages Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Featured Spiritual Packages
            </h2>
            <p className="text-lg text-gray-600">
              Embark on a divine journey to sacred destinations
            </p>
          </div>
          {spiritualPackagesLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
              <p className="mt-4 text-gray-600">Loading spiritual packages...</p>
            </div>
          ) : spiritualPackagesError ? (
            <div className="text-center py-12">
              <p className="text-red-600">{spiritualPackagesError}</p>
            </div>
          ) : featuredSpiritualPackages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No spiritual packages available at the moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredSpiritualPackages.map((pkg) => {
                return (
                  <div 
                    key={pkg.id} 
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/spiritual/${pkg.id}`)}
                  >
                    {pkg.image_url ? (
                      <div className="h-48 w-full overflow-hidden">
                        <img 
                          src={pkg.image_url} 
                          alt={pkg.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-light-blue to-light-orange p-12 text-center">
                        <div className="flex justify-center text-primary-blue">
                          <LotusIcon className="w-20 h-20" />
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 hover:text-primary-orange transition-colors">{pkg.title}</h3>
                      <p className="text-primary-blue font-semibold mb-2 flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4" />
                        {pkg.destination}
                      </p>
                      <p className="text-gray-600 text-sm mb-4 flex items-center gap-2">
                        <ClockIcon className="w-4 h-4" />
                        {pkg.duration}
                      </p>
                      <p className="text-gray-600 mb-4 leading-relaxed">{pkg.description}</p>
                      
                      {pkg.highlights && Array.isArray(pkg.highlights) && pkg.highlights.length > 0 && (
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-2">
                            {pkg.highlights.slice(0, 3).map((highlight, idx) => (
                              <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                {highlight}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t border-gray-200">
                        <div>
                          <span className="text-2xl font-bold text-primary-orange">{pkg.price}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/spiritual/${pkg.id}`);
                          }}
                          className="px-6 py-2.5 bg-primary-orange text-white rounded-lg font-semibold hover:bg-primary-blue transition-all duration-300 text-sm whitespace-nowrap"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-center">
            <Link 
              to="/spiritual" 
              className="inline-block px-8 py-4 bg-primary-blue text-white rounded-xl font-semibold hover:bg-dark-blue transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              View All Spiritual Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              What Our Customers Say
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Real experiences from travelers who chose Happy Yatra Travels
            </p>
          </div>

          {reviewsLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
              <p className="mt-4 text-gray-600">Loading reviews...</p>
            </div>
          ) : reviewsError ? (
            <div className="text-center py-12">
              <p className="text-red-600">{reviewsError}</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No reviews available at the moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {reviews.slice(0, 6).map((review) => (
                <div 
                  key={review.id}
                  className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center mr-4">
                      <span className="text-2xl font-bold text-white">{review.name.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{review.name}</h4>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <LocationIcon className="w-3 h-3" />
                        {review.location}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center mb-3">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        className={`w-5 h-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                        filled={i < review.rating}
                      />
                    ))}
                  </div>

                  <p className="text-sm text-primary-blue font-semibold mb-3">{review.package_name}</p>
                  
                  <p className="text-gray-700 leading-relaxed mb-4 italic">
                    "{review.review}"
                  </p>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500">Verified Customer</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center">
            <div className="inline-flex items-center gap-4 bg-gradient-to-r from-primary-blue to-primary-orange text-white px-8 py-4 rounded-xl shadow-lg">
              <div className="text-3xl font-bold">4.9</div>
              <div className="text-left">
                <div className="flex items-center mb-1">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-yellow-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-sm">Based on {reviews.length}+ customer reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Destinations Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Popular Destinations
            </h2>
            <p className="text-lg text-gray-600">
              Explore India's most beautiful destinations with our curated packages
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {popularDestinations.map((destination, index) => {
              const IconComponent = destination.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl text-center transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl border border-gray-100 cursor-pointer"
                >
                  <div className="flex justify-center mb-4 text-primary-orange">
                    <IconComponent className="w-12 h-12" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{destination.name}</h3>
                  <p className="text-sm text-primary-blue font-semibold mb-1">{destination.packages}</p>
                  <p className="text-xs text-gray-600">{destination.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Planning your dream vacation is simple with Happy Yatra Travels
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div
                  key={index}
                  className="relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100"
                >
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-primary-orange text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                    {step.step}
                  </div>
                  <div className="flex justify-center mb-6 text-primary-blue">
                    <IconComponent className="w-16 h-16" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-center">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Special Offers Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-orange to-orange-600 text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Special Offers & Deals
            </h2>
            <p className="text-xl text-white/90">
              Don't miss out on these exclusive travel deals
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-4xl font-bold mb-2">Early Bird</div>
              <div className="text-2xl font-semibold mb-4">Save up to 20%</div>
              <p className="text-white/90 mb-6">Book 60 days in advance and enjoy special discounts on select packages.</p>
              <div className="bg-white/20 rounded-lg p-3 text-sm">
                <strong>Valid until:</strong> March 31, 2026
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-4xl font-bold mb-2">Group Travel</div>
              <div className="text-2xl font-semibold mb-4">Up to 25% Off</div>
              <p className="text-white/90 mb-6">Travel with 4+ people and get amazing group discounts on all packages.</p>
              <div className="bg-white/20 rounded-lg p-3 text-sm">
                <strong>Minimum:</strong> 4 travelers
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-4xl font-bold mb-2">Last Minute</div>
              <div className="text-2xl font-semibold mb-4">Up to 30% Off</div>
              <p className="text-white/90 mb-6">Spontaneous traveler? Grab our last-minute deals and save big on your trip.</p>
              <div className="bg-white/20 rounded-lg p-3 text-sm">
                <strong>Book within:</strong> 7 days of travel
              </div>
            </div>
          </div>
          <div className="text-center mt-12">
            <a 
              href="https://wa.me/917718838808?text=Hi, I'm interested in special offers"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-white text-primary-orange rounded-xl font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span className="flex items-center gap-2">
                <WhatsAppIcon className="w-5 h-5" />
                Get Best Deals
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Travel Tips Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Travel Tips & Insights
            </h2>
            <p className="text-lg text-gray-600">
              Expert advice to make your journey smooth and memorable
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {travelTips.map((tip, index) => {
              const IconComponent = tip.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-gray-100"
                >
                  <div className="flex justify-center mb-4 text-primary-blue">
                    <IconComponent className="w-10 h-10" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">{tip.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed text-center">{tip.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary-blue to-dark-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '10,00+', label: 'Happy Travelers', Icon: PlaneIcon },
              { number: '100+', label: 'Destinations', Icon: GlobeIcon },
              { number: '5+', label: 'Years Experience', Icon: TrophyIcon },
              { number: '98%', label: 'Satisfaction Rate', Icon: StarIcon }
            ].map((stat, index) => {
              const IconComponent = stat.Icon;
              return (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-4 text-white">
                    <IconComponent className="w-12 h-12" />
                  </div>
                  <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                  <div className="text-lg text-white/90">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Detailed Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Why Travelers Choose Us
            </h2>
            <p className="text-lg text-gray-600">
              We go the extra mile to make your journey unforgettable
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary-orange flex items-center justify-center flex-shrink-0">
                    <CheckIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Best Price Guarantee</h3>
                    <p className="text-gray-600">
                      We offer the best prices in the market. If you find a lower price elsewhere, we'll match it and give you an additional discount.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary-blue flex items-center justify-center flex-shrink-0">
                    <CheckIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">24/7 Customer Support</h3>
                    <p className="text-gray-600">
                      Our dedicated support team is available round the clock to assist you with any queries or emergencies during your trip.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary-orange flex items-center justify-center flex-shrink-0">
                    <CheckIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Flexible Cancellation</h3>
                    <p className="text-gray-600">
                      We understand plans can change. Enjoy flexible cancellation policies with full refund options on most packages.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary-blue flex items-center justify-center flex-shrink-0">
                    <CheckIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Customized Itineraries</h3>
                    <p className="text-gray-600">
                      Tell us your preferences and we'll create a personalized itinerary that matches your travel style and budget perfectly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-blue to-primary-orange p-8 rounded-2xl text-white">
              <h3 className="text-2xl font-bold mb-6">Our Commitment</h3>
              <p className="text-lg leading-relaxed mb-6">
                At Happy Yatra Travels, we believe that every journey should be extraordinary. Our team of travel experts works tirelessly to curate experiences that create lasting memories.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <TargetIcon className="w-6 h-6" />
                  <span>100% Verified Hotels & Services</span>
                </div>
                <div className="flex items-center gap-3">
                  <LockIcon className="w-6 h-6" />
                  <span>Secure & Safe Booking Process</span>
                </div>
                <div className="flex items-center gap-3">
                  <CreditCardIcon className="w-6 h-6" />
                  <span>Multiple Payment Options</span>
                </div>
                <div className="flex items-center gap-3">
                  <DeviceMobileIcon className="w-6 h-6" />
                  <span>Easy Mobile Booking</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Get In Touch
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Have questions? Send us a message and we'll get back to you soon
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-gray-800 font-semibold mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-800 font-semibold mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-gray-800 font-semibold mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                    placeholder="+91 77188 38808"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-gray-800 font-semibold mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                  >
                    <option value="">Select a subject</option>
                    <option value="package-inquiry">Package Inquiry</option>
                    <option value="custom-package">Custom Package Request</option>
                    <option value="booking">Booking Assistance</option>
                    <option value="general">General Inquiry</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-800 font-semibold mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors resize-vertical"
                  placeholder="Tell us about your travel plans or any questions you have..."
                ></textarea>
              </div>

              {isSubmitted && (
                <div className="bg-green-500 text-white p-4 rounded-xl text-center font-semibold">
                  <span className="flex items-center justify-center gap-2">
                    <CheckIcon className="w-5 h-5" />
                    Message sent successfully! We'll get back to you soon.
                  </span>
                </div>
              )}

              {error && (
                <div className="bg-red-500 text-white p-4 rounded-xl text-center font-semibold">
                  {error}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="flex-1 px-8 py-4 bg-primary-orange text-white rounded-xl font-semibold hover:bg-primary-blue transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                <a 
                  href="https://wa.me/917718838808" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1 px-8 py-4 bg-[#25d366] text-white rounded-xl font-semibold hover:bg-[#20ba5a] transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-center"
                >
                  <span className="flex items-center gap-2">
                    <WhatsAppIcon className="w-5 h-5" />
                    WhatsApp Direct
                  </span>
                </a>
              </div>
            </form>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-center text-gray-600 mb-4">Or contact us directly:</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="tel:+917718838808" 
                  className="px-6 py-3 bg-primary-blue text-white rounded-lg font-semibold hover:bg-dark-blue transition-all duration-300"
                >
                  <span className="flex items-center gap-2">
                    <PhoneIcon className="w-5 h-5" />
                    +91 77188 38808
                  </span>
                </a>
                <a 
                  href="mailto:info@happyyatratravels.in" 
                  className="px-6 py-3 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-all duration-300"
                >
                  <span className="flex items-center gap-2">
                    <EmailIcon className="w-5 h-5" />
                    info@happyyatratravels.in
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-blue via-dark-blue to-primary-blue relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E")`
          }}
        ></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">
            Ready to Plan Your Next Adventure?
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-white/90">
            Get in touch with us today and let's create memories together
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/917718838808" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-8 py-4 bg-primary-orange text-white rounded-xl font-semibold hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span className="flex items-center gap-2">
                <WhatsAppIcon className="w-5 h-5" />
                Chat on WhatsApp
              </span>
            </a>
            <a 
              href="tel:+917718838808" 
              className="px-8 py-4 bg-white text-primary-blue rounded-xl font-semibold hover:bg-gray-100 border-2 border-white transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span className="flex items-center gap-2">
                <PhoneIcon className="w-5 h-5" />
                Call Us Now
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;


